import React, { Component } from 'react';
import { Modal, Row, Col, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { inject, observer } from 'mobx-react';
import moment from 'moment';
import Switch from 'react-switch';
import { longDateFormat } from '../../../utils/dateFormat';

//components
import withLocalization from '../../../hoc/withLocalization';
import DatePicker from '../../../components/DatePicker/DatePicker';

//elements
import Button from '~/components/CustomButton/CustomButton.jsx';
import Select from '../../../elements/Select';
import CustomMultiSelect from '../../../elements/CustomMultiSelect';
import LoadingSpinner from '~/elements/LoadingSpinner';

//assets
import CloseModalIcon from '../../../assets/img/CloseApproveModalIcon.svg';

//services
import agent from '~/library/agent';
import { CurrentUserGroupsWidget } from '../../../components/GenericForm/Widgets/UserGroupWidget';

const getCurrentCycleStartDate = (rule_cycle_start_date, cycle_week_length, currentDate = moment()) => {
    const ruleStart = moment(rule_cycle_start_date);
    const weeksSinceStart = currentDate.diff(ruleStart, 'weeks');
    const fullCyclesPassed = Math.floor(weeksSinceStart / cycle_week_length);
    return ruleStart.clone().add(fullCyclesPassed * cycle_week_length, 'weeks');
}

const getUserOptions = (resources) => {
    return resources.map((user) => ({
        label: user.name,
        value: user.id,
        employee_type: user.employee_type,
        isAvailable: true
    }));
}

class DownloadExcelModal extends Component {
    constructor(props) {
        super(props);

        this.state = {
            user: null,
            isLoading: true,
            resources: [],
            departmentOptions: [],
            selectedDept: '',
            user_group: '',
            usersByRoleOptions: [],
            selectedUserIds: [],
            selectedUsers: [],
            userWorkHoursRuleByRole: {},
            groupDetails: [],
            approvedTimesheetsOnly: false,
            publishedRosterOnly: false,
            from_date: moment().startOf('month').format('YYYY-MM-DD'),
            to_date: moment().endOf('month').format('YYYY-MM-DD'),
            isDisable: false,
        };
        this.scheduleRef = React.createRef();
    }

    componentDidMount() {
        this.loadData();
    }

    loadData = async () => {
        const { scheduleStore, clientStore, commonStore, t, userGroupStore } = this.props;
        const { resources } = scheduleStore;

        await clientStore.getWorkHoursRule();
        const groupDetails = await userGroupStore.getGroupDetails({ include_user_details: true });
        const { workHoursRule } = clientStore;
        if (!workHoursRule.length) {
            commonStore.addNotification(t('Not have work hour rules yet'));
        }

        const department = commonStore?.config?.client?.data?.roles;
        const departmentOptions = department.map((dept) => ({ label: dept.name, value: dept.code }));
        departmentOptions.unshift({ label: 'Others', value: 'z_none' });
        departmentOptions.unshift({ label: t('All'), value: '' });
        const usersByRoleOptions = getUserOptions(resources);
        usersByRoleOptions.shift();
        this.setState(
            {
                resources,
                departmentOptions,
                usersByRoleOptions,
                groupDetails
            },
            () => {
                this.setState({ isLoading: false });
            }
        );
    };

    handleUserSelection = (selectedIds) => {
        const { usersByRoleOptions, resources, selectedDept } = this.state;
        const selectedUsers = usersByRoleOptions.filter((user) => selectedIds.includes(user.value));
        if (selectedIds.length === 0) {
            const filteredUsers = getUserOptions(resources).filter(r => selectedDept ? r.value && r.employee_type === selectedDept : r.value);
            this.setState({ user_group: '', usersByRoleOptions: filteredUsers, selectedUsers: [], selectedUserIds: [] });
            return;
        }
        this.setState({
            selectedUserIds: selectedIds,
            selectedUsers: selectedUsers,
            user_group: this.state.user_group
        });
    };

    handleRoleChange = (name, value) => {
        const { clientStore, commonStore, t, scheduleStore } = this.props;
        const { resources: storeResources } = scheduleStore;
        const { workHoursRule } = clientStore;
        const { resources } = this.state;
        let selectedUsers = [];
        let selectedUserIds = [];
        if (!value) {
            const usersByRoleOptions = getUserOptions(storeResources)
            this.setState({
                from_date: moment().startOf('month').format('YYYY-MM-DD'),
                to_date: moment().endOf('month').format('YYYY-MM-DD'),
                usersByRoleOptions,
                selectedUsers,
                selectedUserIds,
                [name]: value,
            })
            return;
        }

        const usersByRoleOptions = getUserOptions(resources).filter((user) => user.employee_type === value);
        if (usersByRoleOptions.length === 0 || !usersByRoleOptions) {
            this.setState({
                usersByRoleOptions,
                isDisable: true,
                user_group: '',
                [name]: value,
            });
            return;
        }
        const userWorkHoursRuleByRole = workHoursRule.find((rule) => rule.department === value);
        if (!userWorkHoursRuleByRole) {
            commonStore.addNotification(t('No workhour rule is defined for this department'), null, 'error');
            this.setState({
                isDisable: true,
                user_group: '',
                [name]: value,
            });
            return;
        }
        const { cycle_week_length, rule_cycle_start_date } = userWorkHoursRuleByRole || {};
        userWorkHoursRuleByRole.currentCycleStartDate = getCurrentCycleStartDate(
            rule_cycle_start_date,
            cycle_week_length
        );
        userWorkHoursRuleByRole.currentCycleEndDate = moment(userWorkHoursRuleByRole.currentCycleStartDate)
            .add(cycle_week_length, 'weeks')
            .add(-1, 'day')
            .format('YYYY-MM-DD');

        if (value !== 'z_none') {
            selectedUsers = getUserOptions(resources).filter((user) => user.employee_type === value);
            selectedUserIds = selectedUsers?.map((user) => user.value);
            commonStore.addNotification(t('All Users are selected'), null, 'success');
        }
        this.setState({
            usersByRoleOptions,
            selectedUserIds,
            selectedUsers,
            userWorkHoursRuleByRole,
            from_date: userWorkHoursRuleByRole.currentCycleStartDate,
            to_date: userWorkHoursRuleByRole.currentCycleEndDate,
            isDisable: false,
            user_group: '',
            [name]: value,
        });
    };

    handleDownload = async () => {
        const { commonStore, userStore } = this.props;
        const { currentUser } = userStore;
        let pmMembers = '';
        if (currentUser.user_type !== 'admin' && currentUser?.Manages?.length) {
            pmMembers = currentUser.Manages.join(',');
        }
        let payload = {
            authorization: commonStore.token,
            fromDate: this.state.from_date,
            toDate: this.state.to_date,
            department: this.state.selectedDept,
            users: this.state.selectedUserIds.join(',') ? this.state.selectedUserIds.join(',') : pmMembers,
            approvedTimesheetsOnly: this.state.approvedTimesheetsOnly,
            publishedRosterOnly: this.state.publishedRosterOnly
        };

        return agent.Roster.downloadExcelReport(payload).then((res) => {
            const link = res.Link + agent.convertToGetParams({ ...payload, validated: true });
            window.open(link);
        }).catch(error => {
            commonStore.addNotification('Error in downloading excel report', null, 'error');
        });
    };

    filterCycleStartDates = (date) => {
        const { userWorkHoursRuleByRole } = this.state;
        const cycleStart = moment(userWorkHoursRuleByRole.rule_cycle_start_date);
        const cycleLengthWeeks = userWorkHoursRuleByRole.cycle_week_length;
        const d = moment(date);
        if (d.day() !== 1) return false;
        const weeksDiff = d.diff(cycleStart, 'weeks');
        return weeksDiff % cycleLengthWeeks === 0;
    };

    filterCycleEndDates = (date) => {
        const { userWorkHoursRuleByRole } = this.state;
        const cycleStart = moment(userWorkHoursRuleByRole.rule_cycle_start_date);
        const cycleLengthWeeks = userWorkHoursRuleByRole.cycle_week_length;
        const d = moment(date);
        if (d.day() !== 0) return false;
        const weeksDiff = d.diff(cycleStart, 'weeks');
        return (weeksDiff + 1) % cycleLengthWeeks === 0;
    };

    handleChange = (name, data) => {
        const { userWorkHoursRuleByRole, resources, groupDetails, selectedDept } = this.state;
        if (name === 'from_date') {
            if (moment(this.state.to_date).diff(moment(data), 'days') < 0) {
                this.setState({ to_date: moment(data).add(userWorkHoursRuleByRole.cycle_week_length, 'weeks').add(-1, 'day') });
            }
        }
        if (name === 'to_date') {
            if (moment(data).diff(moment(this.state.from_date), 'days') < 0) {
                this.setState({ from_date: moment(data).subtract(userWorkHoursRuleByRole.cycle_week_length, 'weeks').add(1, 'day') });
            }
        }
        if (name === 'user_group') {
            const filteredUsers = getUserOptions(resources).filter(r => selectedDept
                ? r.value && r.employee_type === selectedDept && (data > 0 ? (!!groupDetails.find(g => g.code === data && g.userIds.includes(r.value))) : true)
                : r.value && (data > 0 ? (!!groupDetails.find(g => g.code === data && g.userIds.includes(r.value))) : true)
            );
            if (data > 0)
                this.setState({ usersByRoleOptions: filteredUsers, selectedUsers: filteredUsers, selectedUserIds: filteredUsers.map(u => u.value) })
            else
                this.setState({ usersByRoleOptions: filteredUsers, selectedUsers: [], selectedUserIds: [] })
        }
        this.setState({ [name]: data });
    };

    render() {
        const { commonStore, show, handleClose, t } = this.props;
        const {
            isLoading, usersByRoleOptions, selectedUserIds, departmentOptions,
            selectedDept, approvedTimesheetsOnly, publishedRosterOnly, from_date, to_date, user_group
        } = this.state;
        const dateTimeRules = commonStore.getDateTimeRules();

        return (
            <div>
                <Modal size="xl" centered show={show} onHide={handleClose}>
                    <Modal.Header className="set_ModalHeader">
                        <Modal.Title className="w-100 text-center custom-modal-title">
                            {t('Download Excel File')}
                        </Modal.Title>
                        <img src={CloseModalIcon} className="cursor-pointer" onClick={handleClose} alt="close-btn" />
                    </Modal.Header>
                    <Modal.Body style={{ height: 420 }}>
                        {isLoading ? (
                            <LoadingSpinner />
                        ) : (
                            <div className="px-3" style={{ minHeight: '500px' }}>
                                <div>
                                    <Row className="d-flex justify-content-center align-items-end">
                                        <Col md={4}>
                                            <label className="control-label mb-1">
                                                {t('Select')} {t('Department')}
                                            </label>
                                            <Select
                                                options={departmentOptions}
                                                value={departmentOptions.find((item) => item.value === selectedDept)}
                                                onChange={(e) => this.handleRoleChange('selectedDept', e.value)}
                                            />
                                        </Col>
                                        <Col md={4}>
                                            <label className="control-label mb-1">
                                                {`${t('Select')} ${t('Group')}`}
                                            </label>
                                            <CurrentUserGroupsWidget
                                                value={user_group}
                                                onChange={value => this.handleChange('user_group', value)}
                                            />
                                        </Col>
                                        <Col md={4}>
                                            <label className="control-label mb-1">
                                                {`${t('Select')} ${t('Employee')}`}
                                            </label>
                                            {selectedDept && !usersByRoleOptions.length && (
                                                <span className="text-danger fw-600 font-size-10 ms-2">
                                                    {t('No users in this department')}.
                                                </span>
                                            )}
                                            <CustomMultiSelect
                                                options={usersByRoleOptions || []}
                                                value={selectedUserIds || []}
                                                onChange={this.handleUserSelection}
                                            />
                                        </Col>
                                    </Row>

                                    <Row className="pt-3 pb-2">
                                        <Col md={4}>
                                            <label className="control-label mb-1">
                                                {t('From')} {t('Week')}
                                            </label>
                                            <DatePicker
                                                selected={from_date && new Date(from_date)}
                                                onChange={(date) => this.handleChange('from_date', date)}
                                                dateFormat={longDateFormat(dateTimeRules)}
                                                filterDate={selectedDept && this.filterCycleStartDates}
                                            />
                                        </Col>
                                        <Col md={4}>
                                            <label className="control-label mb-1">
                                                {t('To')} {t('Week')}
                                            </label>
                                            <DatePicker
                                                selected={to_date && new Date(to_date)}
                                                onChange={(date) => this.handleChange('to_date', date)}
                                                dateFormat={longDateFormat(dateTimeRules)}
                                                filterDate={selectedDept && this.filterCycleEndDates}
                                            />
                                        </Col>
                                    </Row>
                                    <Row className="mt-4">
                                        <Col className="mt-1" sm={1}>
                                            <Switch
                                                checked={approvedTimesheetsOnly}
                                                height={20}
                                                width={40}
                                                uncheckedIcon={false}
                                                checkedIcon={false}
                                                onColor={'#2550AC'}
                                                onChange={(checked) => this.setState({ approvedTimesheetsOnly: checked })}
                                            />
                                        </Col>
                                        <Col sm={11}>
                                            <label className="control-label">{t('Approved timesheets only')}</label>
                                            <OverlayTrigger
                                                placement="right"
                                                overlay={
                                                    <Tooltip id="tooltip-top">
                                                        {t(
                                                            'By Enabling this feature you can download approved timesheets only'
                                                        )}
                                                    </Tooltip>
                                                }
                                            >
                                                <i
                                                    className="fa fa-info-circle text-muted ms-1"
                                                    style={{ opacity: '0.5' }}
                                                    aria-hidden="true"
                                                ></i>
                                            </OverlayTrigger>
                                        </Col>
                                    </Row>
                                    <Row className="mt-4">
                                        <Col className="mt-1" sm={1}>
                                            <Switch
                                                checked={publishedRosterOnly}
                                                height={20}
                                                width={40}
                                                uncheckedIcon={false}
                                                checkedIcon={false}
                                                onColor={'#2550AC'}
                                                onChange={(checked) => this.setState({ publishedRosterOnly: checked })}
                                            />
                                        </Col>
                                        <Col sm={11}>
                                            <label className="control-label">{t('Published Roster only')}</label>
                                            <OverlayTrigger
                                                placement="right"
                                                overlay={
                                                    <Tooltip id="tooltip-top">
                                                        {t(
                                                            'By Enabling this feature you can download published roster only'
                                                        )}
                                                    </Tooltip>
                                                }
                                            >
                                                <i
                                                    className="fa fa-info-circle text-muted ms-1"
                                                    style={{ opacity: '0.5' }}
                                                    aria-hidden="true"
                                                ></i>
                                            </OverlayTrigger>
                                        </Col>
                                    </Row>
                                </div>
                            </div>
                        )}
                    </Modal.Body>
                    <Modal.Footer>
                        <Button
                            variant="secondary"
                            onClick={this.handleDownload}
                            className="btn-wd"
                            disabled={this.state.isDisable}
                        >
                            {t('Download')}
                        </Button>
                        <Button variant="secondary" onClick={handleClose} className="btn-wd">
                            {t('Close')}
                        </Button>
                    </Modal.Footer>
                </Modal>
            </div>
        );
    }
}

export default inject('commonStore', 'scheduleStore', 'clientStore', 'userGroupStore', 'userStore')(withLocalization((observer(DownloadExcelModal))));